import React, { useState, useEffect, useMemo } from 'react';

import { ToggleSwitch } from 'components/ToggleSwitch';
import MeshAreaChart from 'components/graph/MeshAreaChart';
import MeshBarChart from 'components/graph/MeshBarGraph';
import { CircularLoader, ErrorMessage, NoGraphData } from 'screens/analytics/commonComponents';
import { AbsCenterContainer } from 'screens/analytics/styles';
import { StyledBox, StyledGrid, StyledBorderedPaper } from 'styles/common';

import { getColorFromKey } from 'constants/helper';
import { EmployeeCheckInIcon, EmployeeProgressIcon } from 'constants/icons';
import { sendClickedInsightsGraphOfGoalsEvent } from 'constants/mixpanelHelper';

import { getAggregatedScoreForIndividual } from 'services/analytics/goal';

import { useGoalAnalytics } from '../GoalAnalyticsContext';

const CHART_OPTIONS = {
  PROGRESS: {
    id: 'PROGRESS',
    icon: <EmployeeProgressIcon size="header" />,
  },
  CHECKIN: {
    id: 'CHECKIN',
    icon: <EmployeeCheckInIcon size="header" />,
  },
};

/**
 * @deprecated 06-08-2024
 */
const EmployeeCheckinAndProgressChart = () => {
  const [loading, setLoading] = useState(true);
  const [viewTypeId, setViewTypeId] = useState(CHART_OPTIONS.CHECKIN.id);

  const [chartData, setChartData] = useState(null);
  const {
    state: {
      currentState: { selectedCohort, selectedGroupByKey, selectedSplitByKey, selectedTimeBoundPeriods },
    },
  } = useGoalAnalytics();

  const fetchAggregatedData = () => {
    setLoading(true);
    setChartData(null);

    getAggregatedScoreForIndividual({
      cohort: selectedCohort,
      groupByKeys: selectedGroupByKey,
      splitByKeys: selectedSplitByKey,
      viewType: viewTypeId,
      timeBounds: selectedTimeBoundPeriods,
    })
      .then((res) => {
        setChartData(res);
      })
      .catch((err) => {
        console.log(err || `Call to fetch data for ${viewTypeId} chart failed.`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (selectedCohort !== null && selectedGroupByKey !== null && selectedSplitByKey !== null) fetchAggregatedData();
  }, [selectedCohort, selectedGroupByKey, selectedSplitByKey, selectedTimeBoundPeriods, viewTypeId]);

  return (
    <StyledBorderedPaper
      flexcontainer
      alignitems="flex-end"
      style={{
        height: '100%',
        position: 'relative',
      }}
    >
      {loading ? (
        <CircularLoader />
      ) : chartData === null ? (
        <ErrorMessage errorText="Some error occurred." />
      ) : chartData.length === 0 ? (
        <AbsCenterContainer>
          <NoGraphData title="" subTitle="No data found in this cohort and timeline" />
        </AbsCenterContainer>
      ) : (
        <StyledBox
          padding="1.5rem 2rem 1rem 1rem"
          width="100%"
          height="100%"
          position="relative"
          display="flex"
          flexDirection="column"
        >
          <StyledGrid container justify="flex-end">
            <ToggleSwitch
              selectedId={viewTypeId}
              onIconClick={(newViewTypeId) => {
                sendClickedInsightsGraphOfGoalsEvent();
                setViewTypeId(newViewTypeId);
              }}
              iconsList={Object.values(CHART_OPTIONS)}
            />
          </StyledGrid>
          {viewTypeId === CHART_OPTIONS.CHECKIN.id ? (
            <EmployeesVSCheckinChart data={chartData} />
          ) : (
            <EmployeesVSProgressChart data={chartData} />
          )}
        </StyledBox>
      )}
    </StyledBorderedPaper>
  );
};

const EmployeesVSCheckinChart = ({ data }) =>
  useMemo(() => {
    const mappedData = data.map(({ count, score }) => ({
      displayName: score,
      count,
    }));

    return (
      <MeshAreaChart
        chartProps={{
          width: '100%',
          height: '100%',
        }}
        xAxisLabelProps={{
          value: 'Number of check-ins',
        }}
        yAxisLabelProps={{
          value: 'Number of people',
        }}
        data={mappedData}
      />
    );
  }, [data]);

const EmployeesVSProgressChart = ({ data }) =>
  useMemo(() => {
    const mappedData = data.map(({ key, count, color }) => ({
      displayName: key,
      color: getColorFromKey(color),
      count,
    }));

    return (
      <MeshBarChart
        xAxisLabelProps={{
          value: 'Progress',
        }}
        yAxisLabelProps={{
          value: 'Number of people',
        }}
        data={mappedData}
      />
    );
  }, [data]);

export default EmployeeCheckinAndProgressChart;
